import { assign, createMachine, fromPromise } from 'xstate'

import Api from '@/api/_legacy'
import {
  ApiAddDfmUser,
  ApiEditUser,
  UserInformationBasic,
  UserInformationBasicDfm
} from '@/api/_legacy/generated-api'
import { UserType } from '@/types/user'

import { setErrorsAction } from '../../common/set-errors-action'
import { Mutation } from '../../types'
import { EMAIL_REGEX, TENANT_ID } from './utils'

export type EditDfmUserState = {
  errorMessages: string[]
  errors: { [key: string]: string }
  initialUser?: UserInformationBasic
  isSaveEnabled: boolean
  isSaveSuccess: boolean
  notifyParentOfSave?: () => void
  user?: UserInformationBasic
}

type InitEvent = {
  notifyParentOfSave: () => void
  type: 'INIT'
  user: UserInformationBasic
}

type InitAddEvent = {
  notifyParentOfSave: () => void
  type: 'INIT_ADD'
}

type MutateEvent = {
  mutation: Mutation<UserInformationBasic>
  type: 'MUTATE'
}

type SaveEvent = {
  type: 'SAVE'
  user: UserInformationBasicDfm
}

type SaveNewUserEvent = {
  dfmId?: string
  type: 'SAVE_NEW_USER'
  user: UserInformationBasicDfm
  userType: UserType
}

export type EditDfmUserEvent = InitEvent | InitAddEvent | MutateEvent | SaveEvent | SaveNewUserEvent

export const editDfmUserMachine = createMachine(
  {
    types: {} as {
      context: EditDfmUserState
      events: EditDfmUserEvent
    },
    id: 'edit-dfm-user-machine',
    initial: 'idle',
    context: {
      isSaveSuccess: false,
      isSaveEnabled: false,
      user: {
        userId: '',
        userName: '',
        firstName: '',
        lastName: '',
        email: '',
        inactive: false,
        portfolioManagerCategory: 'None',
        status: 'Active',
        roles: ['PortfolioManager']
      },
      errors: {},
      errorMessages: []
    },
    states: {
      idle: {
        on: {
          INIT: {
            actions: ['setUser', 'setNotifyParentOfSave'],
            target: 'userLoaded'
          },
          INIT_ADD: {
            target: 'userLoaded',
            actions: ['resetUser', 'setNotifyParentOfSave']
          }
        }
      },
      userLoaded: {
        on: {
          MUTATE: {
            actions: ['mutate', 'setDirty'],
            target: 'userLoaded'
          },
          SAVE: {
            target: 'saving',
            actions: ['resetErrors']
          },
          SAVE_NEW_USER: {
            target: 'savingNewUser',
            actions: ['resetErrors']
          }
        }
      },
      saving: {
        invoke: {
          id: 'saveUser',
          src: 'saveUser',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            actions: ['clearErrors', 'setSuccess', 'notifyParent'],
            target: 'userLoaded'
          },
          onError: {
            actions: ['setError'],
            target: 'userLoaded'
          }
        }
      },
      savingNewUser: {
        invoke: {
          id: 'saveNewUser',
          src: 'saveNewUser',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            actions: ['clearErrors', 'setSuccess', 'notifyParent', 'resetUser'],
            target: 'userLoaded'
          },
          onError: {
            actions: ['setError'],
            target: 'userLoaded'
          }
        }
      }
    }
  },
  {
    actors: {
      saveUser: fromPromise(
        async ({ input }: { input: { context: EditDfmUserState; event: SaveEvent } }) => {
          const { event } = input
          const user = event.user as UserInformationBasicDfm

          const errors: { [key: string]: string } = {}
          if (user.firstName.length < 1) {
            errors.firstName = 'First name must be provided'
          }

          if (user.lastName.length < 1) {
            errors.lastName = 'Last name must be provided'
          }

          if (Object.keys(errors).length > 0) {
            throw new Error('validation')
          }

          const userToSave: ApiEditUser = {
            userName: user.userName,
            firstName: user.firstName,
            lastName: user.lastName,
            portfolioManagerCategory: user.portfolioManagerCategory
          }

          try {
            const response = await Api.usermanager.usermanagerUpdate(userToSave)
            const { data } = response
            return data
          } catch (error) {
            throw {
              error: 'Failed to create user'
            }
          }
        }
      ),
      saveNewUser: fromPromise(
        async ({ input }: { input: { context: EditDfmUserState; event: SaveNewUserEvent } }) => {
          const { event } = input
          const user = event.user as UserInformationBasicDfm

          const errors: { [key: string]: string } = {}
          if (user.firstName.length < 1) {
            errors.firstName = 'First name must be provided'
          }

          if (user.lastName.length < 1) {
            errors.lastName = 'Last name must be provided'
          }

          if (user.email.length < 1 || (user.email && !user.email.match(EMAIL_REGEX))) {
            errors.email = 'Email must be valid'
          }

          if (
            user.portfolioManagerCategory === 'None' ||
            user.portfolioManagerCategory.length < 1
          ) {
            errors.portfolioManagerCategory = 'Category must be provided'
          }

          if (Object.keys(errors).length > 0) {
            throw {
              type: 'validation',
              errors
            }
          }

          const userToSave: ApiAddDfmUser = {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            portfolioManagerCategory: user.portfolioManagerCategory
          }

          try {
            const { dfmId } = event
            const response = await Api.tenant.usersCreate3(dfmId, TENANT_ID, userToSave)
            const { data } = response
            return data
          } catch (error) {
            throw {
              error: 'Failed to create user'
            }
          }
        }
      )
    },
    actions: {
      notifyParent: assign(({ context }) => {
        context.notifyParentOfSave?.()
        return {}
      }),
      setUser: assign(({ event }: any) => ({
        user: event.user
      })),
      setNotifyParentOfSave: assign(({ event }: any) => ({
        notifyParentOfSave: event.notifyParentOfSave
      })),
      resetUser: assign(context => ({
        ...context,
        user: {
          userId: '',
          userName: '',
          firstName: '',
          lastName: '',
          email: '',
          inactive: false,
          status: 'Active',
          roles: [],
          portfolioManagerCategory: 'None'
        }
      })),
      mutate: assign(({ context, event }: any) => {
        const user = { ...context.user }
        event.mutation(user)

        return {
          ...context,
          user
        }
      }),
      setDirty: assign(context => ({
        ...context,
        isSaveEnabled: true
      })),
      resetErrors: assign(context => ({
        ...context,
        errors: {}
      })),
      setError: assign(({ context, event }: any) => setErrorsAction(context, event)),
      clearErrors: assign(context => ({
        ...context,
        errorMessages: [],
        errors: {}
      })),
      setSuccess: assign(context => ({
        ...context,
        isSaveSuccess: true
      }))
    }
  }
)
